'use client';

import { useSearchParams } from 'next/navigation';
import { signIn, useSession } from 'next-auth/react';
import { Suspense, useState, useEffect, useRef, useCallback } from 'react';
import { z } from 'zod';

import { NotificationBanner } from '@/components/auth/NotificationBanner';
import { SignInForm } from '@/components/auth/SignInForm';
import { TotpChallengeForm } from '@/components/auth/TotpChallengeForm';
import { logger } from '@/lib/logger';

type SignInSession = ReturnType<typeof useSession>['data'];
type SignInStatus = ReturnType<typeof useSession>['status'];
/**
 * URLが安全なリダイレクト先かを検証する
 * オープンリダイレクト脆弱性（CWE-601）を防ぐ
 */
function isValidRedirectUrl(url: string): boolean {
  // 空文字列または未定義の場合は安全なデフォルト
  if (!url) return false;

  // `//example.com` は protocol-relative な外部URLとして扱う
  if (url.startsWith('//')) return false;

  // 相対URLは常に安全（/で始まる）
  if (url.startsWith('/')) return true;

  // 絶対URLの場合、同一オリジンのみ許可
  try {
    const urlObj = new URL(url, window.location.origin);
    return urlObj.origin === window.location.origin;
  } catch {
    // URLパースエラーの場合は安全でないと判断
    return false;
  }
}

/**
 * サインイン直後の遷移先を合流点 (/auth/post-signin) に揃える。
 * server 側 redirect callback と同様に、ルート遷移のみ post-signin へ向ける。
 */
function toPostSignInTarget(url: string): string {
  if (!isValidRedirectUrl(url)) return '/auth/post-signin';
  if (url.startsWith('//')) return '/auth/post-signin';
  if (url === '/') return '/auth/post-signin';

  try {
    const parsedUrl = new URL(url, window.location.origin);
    const isSameOrigin = parsedUrl.origin === window.location.origin;
    const isHttpProtocol = parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
    const isRootPath = parsedUrl.pathname === '/' && !parsedUrl.search && !parsedUrl.hash;
    if (!isSameOrigin || !isHttpProtocol) {
      return '/auth/post-signin';
    }

    if (isRootPath) {
      return '/auth/post-signin';
    }
  } catch {
    return '/auth/post-signin';
  }

  return url;
}

/**
 * セッションがリダイレクト可能な最小要件（user.id と user.email）を満たすか判定する。
 * 不完全セッションでの早期リダイレクトを防ぎ、サインイン画面表示を継続する。
 */
function hasCompleteSession(session: SignInSession): boolean {
  return Boolean(session?.user?.email && session?.user?.id);
}

/**
 * サインインページの認証状態デバッグログを開発環境でのみ出力する。
 * 本番環境ではノイズを避けるためログを抑止する。
 */
function logSignInSessionDebug(status: SignInStatus, session: SignInSession): void {
  if (process.env.NODE_ENV !== 'development') return;
  logger.debug('[SignIn] Auth status:', status);
  logger.debug('[SignIn] Session completeness:', {
    hasSession: Boolean(session),
    hasUser: Boolean(session?.user),
    hasUserId: Boolean(session?.user?.id),
    hasUserEmail: Boolean(session?.user?.email),
  });
}

const SIGNIN_MESSAGES: Record<string, string> = {
  'signup-complete': 'アカウントが作成されました。サインインしてください。',
  'password-reset': 'パスワードがリセットされました。新しいパスワードでサインインしてください。',
  'password-changed': 'パスワードを変更しました。新しいパスワードでサインインしてください。',
  'totp-enabled': '認証アプリを設定しました。パスワードと認証アプリのコードでサインインしてください。',
  'totp-disabled': '認証アプリを解除しました。もう一度サインインしてください。',
  'mfa-reauth': '所属する組織が認証アプリでのログインを必須にしています。パスワードと認証アプリのコードでサインインしてください。',
};
// サインインできなかった理由の案内。成功の通知ではなくエラーとして出す
const SIGNIN_REJECTIONS: Record<string, string> = {
  'use-password-signin': 'このアカウントは、この画面のメールアドレスとパスワードでサインインしてください。',
};
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';
const FALLBACK_INVITATION_MESSAGE = '招待を受け入れるにはサインインが必要です';

type InvitationValidationResponse = {
  valid?: boolean;
  workspaceName?: string;
};

/**
 * `/api/auth/login-config?org=<slug>` のレスポンス schema (Issue #6149)。
 *
 * 外部 API 応答 (現状は同 repo だが CDN/middleware を挟むと壊れる可能性がある) を
 * `as` キャストではなく zod で境界検証する (defense in depth)。
 * `null` フィールドは組織側で未設定 → SignInForm のデフォルト表記に fall back する。
 */
const loginConfigResponseSchema = z.object({
  success: z.boolean().optional(),
  title: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  ssoButtonLabel: z.string().nullable().optional(),
  hideCredentials: z.boolean().optional(),
});

/** SignInForm に渡す形に正規化した login page config (Issue #6149)。 */
type LoginPageConfigState = {
  title?: string;
  description?: string;
  ssoButtonLabel?: string;
  hideCredentials: boolean;
};

const DEFAULT_SIGN_IN_ERROR = 'サインインに失敗しました。メールアドレスとパスワードを確認してください。';

/** パスワードログインの開始・確認 API の応答（ADR-0031 の canonical envelope） */
const loginStepResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    status: z.enum(['ready', 'totp_required']).optional(),
    transactionId: z.string().optional(),
    error: z.string().optional(),
    message: z.string().optional(),
  }),
});

type LoginStepData = z.infer<typeof loginStepResponseSchema>['data'];

/** パスワードログインの開始・確認 API を呼ぶ（ADR-0043）。応答は zod で境界検証する。 */
async function postLoginStep(url: string, payload: Record<string, string>): Promise<{ ok: boolean; data: LoginStepData }> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    credentials: 'same-origin',
  });
  const parsed = loginStepResponseSchema.safeParse(await response.json().catch(() => null));
  return { ok: response.ok && parsed.success && parsed.data.success, data: parsed.success ? parsed.data.data : {} };
}

/** organization slug の許容形式。API 側 zod と揃える (`/^[a-z0-9-]+$/` かつ 63 文字以下)。 */
const ORG_SLUG_PATTERN = /^[a-z0-9-]{1,63}$/;

// サブコンポーネント化
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: 認証状態管理と遷移制御を一箇所で扱う既存設計のため
function SignInContent() {
  const searchParams = useSearchParams();
  const { data: session, status } = useSession();
  const hasScheduledRedirectRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // パスワードは通り、TOTP の確認コードを待っているログイン（ADR-0043）
  const [totpTransactionId, setTotpTransactionId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [invitationWorkspaceName, setInvitationWorkspaceName] = useState<string | null>(null);

  // オープンリダイレクト対策：URLを検証してから使用
  const requestedUrl = searchParams?.get('callbackUrl') || searchParams?.get('returnUrl') || '/';
  const returnUrl = isValidRedirectUrl(requestedUrl) ? requestedUrl : '/';
  const sessionUserId = session?.user?.id ?? null;
  const sessionUserEmail = session?.user?.email ?? null;

  // 組織別ログイン画面カスタマイズ (Issue #6149)。
  // `?org=<slug>` が付いていて、slug が英数ハイフンの許容形式なら公開 API に問い合わせ、
  // 見出し / 説明 / SSO ボタンラベル / パスワード欄非表示を取得する。fetch 失敗時は
  // 何も渡さず、SignInForm が既存のデフォルト表記でレンダリングする。
  const rawOrgSlug = searchParams?.get('org') ?? null;
  const orgSlug = rawOrgSlug && ORG_SLUG_PATTERN.test(rawOrgSlug) ? rawOrgSlug : null;
  const [loginPageConfig, setLoginPageConfig] = useState<LoginPageConfigState>({
    hideCredentials: false,
  });
  // `?org=<slug>` 付きアクセスでは fetch 完了まで credentials 導線の描画を保留する
  // (Issue #6149 レビュー指摘: hideCredentials=true 組織で初回 paint に通常フォームが
  //  一瞬見えてしまう flicker を防ぐ)。org が無いケースは影響を受けない。
  const [loginConfigLoading, setLoginConfigLoading] = useState<boolean>(orgSlug !== null);

  // セキュリティイベント：不正なリダイレクトURLの検知
  if (requestedUrl !== returnUrl && process.env.NODE_ENV === 'development') {
    logger.warn('[Security] Invalid redirect URL blocked:', requestedUrl);
  }

  const verified = searchParams?.get('verified') === 'true';
  const messageKey = searchParams?.get('message') ?? null;
  const invitationToken = searchParams?.get('invitation_token') || null;
  const invitationMessage = invitationWorkspaceName
    ? `「${invitationWorkspaceName}」への招待です。サインインして参加しましょう。`
    : FALLBACK_INVITATION_MESSAGE;

  useEffect(() => {
    const text = verified
      ? 'メールアドレスが確認されました。サインインしてください。'
      : messageKey
        ? SIGNIN_MESSAGES[messageKey]
        : null;
    // text が falsy の場合も明示的に null を代入し、前回の通知が残らないようにする
    setMessage(text ?? null);
    const rejection = messageKey ? SIGNIN_REJECTIONS[messageKey] : undefined;
    if (rejection) setError(rejection);
  }, [verified, messageKey]);

  useEffect(() => {
    if (!invitationToken) {
      setInvitationWorkspaceName(null);
      return;
    }

    let cancelled = false;
    fetch(`/api/auth/validate-invitation?token=${encodeURIComponent(invitationToken)}`)
      .then(async (res) => {
        if (!res.ok) return;
        const data = (await res.json()) as InvitationValidationResponse;
        if (!cancelled && data.valid === true && data.workspaceName) {
          setInvitationWorkspaceName(data.workspaceName);
        }
      })
      .catch(() => {
        if (!cancelled) setInvitationWorkspaceName(null);
      });

    return () => {
      cancelled = true;
    };
  }, [invitationToken]);

  // 組織別ログイン画面カスタマイズの取得 (Issue #6149)。fetch 失敗時はサイレント無視。
  useEffect(() => {
    if (!orgSlug) {
      setLoginPageConfig({ hideCredentials: false });
      setLoginConfigLoading(false);
      return;
    }

    setLoginConfigLoading(true);
    let cancelled = false;
    fetch(`/api/auth/login-config?org=${encodeURIComponent(orgSlug)}`)
      .then(async (res) => {
        if (!res.ok) {
          // route.ts は常に 200 を返す設計だが、CDN/middleware 由来の非 200 応答が
          // 起きたときに前 org の設定が残らないよう明示的にフォールバックする。
          if (!cancelled) setLoginPageConfig({ hideCredentials: false });
          return;
        }
        const rawData: unknown = await res.json();
        if (cancelled) return;
        // 形状検証: 想定外の shape が返ってきても壊れないようにデフォルト UI に fall back。
        const parsed = loginConfigResponseSchema.safeParse(rawData);
        if (!parsed.success) {
          setLoginPageConfig({ hideCredentials: false });
          return;
        }
        const data = parsed.data;
        setLoginPageConfig({
          title: data.title ?? undefined,
          description: data.description ?? undefined,
          ssoButtonLabel: data.ssoButtonLabel ?? undefined,
          hideCredentials: data.hideCredentials === true,
        });
      })
      .catch(() => {
        // 公開エンドポイントの失敗はデフォルト UI に fall back。ログイン継続を優先。
        if (!cancelled) setLoginPageConfig({ hideCredentials: false });
      })
      .finally(() => {
        if (!cancelled) setLoginConfigLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [orgSlug]);

  const schedulePostSignInRedirect = useCallback((redirectTarget: string) => {
    if (hasScheduledRedirectRef.current) {
      return;
    }
    hasScheduledRedirectRef.current = true;

    // 合流点 /auth/post-signin は force-dynamic な Server Component で redirect() するため、
    // router.push() の soft-nav では RSC fetch が redirect を確定できず URL が /auth/signin に
    // 留まったままループする (#5237: post-signin への _rsc が連打される)。
    // フルナビゲーションでブラウザに HTTP リダイレクトとして解決させ、URL を確定させる。
    window.location.assign(redirectTarget);
  }, []);

  // 既に認証済みの場合はリダイレクト
  useEffect(() => {
    logSignInSessionDebug(status, session);
  }, [status, session]);

  useEffect(() => {
    if (status === 'unauthenticated') {
      if (IS_DEVELOPMENT) {
        logger.debug('[SignIn] User is unauthenticated, staying on signin page');
      }
      return;
    }

    if (status !== 'authenticated' || !sessionUserId || !sessionUserEmail) {
      return;
    }

    if (IS_DEVELOPMENT) {
      logger.debug('[SignIn] Valid session detected, redirecting to:', returnUrl);
    }

    schedulePostSignInRedirect(toPostSignInTarget(returnUrl));
  }, [status, sessionUserId, sessionUserEmail, returnUrl, schedulePostSignInRedirect]);

  // SSO サインインハンドラー（Cognito Hosted UI 経由）
  // Issue #2812 / ADR-0029。SSO サインイン入口は全環境・全ユーザーに常時表示する
  // （旧: SSO_FEATURE_ALLOWED_ORG_IDS allowlist + 本番ガードで出し分けていたが GA 化）。
  // Hosted UI 内で IdP（SAML / OIDC）が選択できる前提。SSO 強制テナントの
  // ユーザーが入口に迷わないよう、メール/パスワードフォームと併設する。
  const handleSsoSignIn = () => {
    setLoading(true);
    setError(null);
    // signIn('cognito') で next-auth が Cognito Hosted UI へリダイレクトする。
    // callbackUrl は Hosted UI でのログイン成功後の遷移先。
    void signIn('cognito', { callbackUrl: returnUrl });
  };

  // ready になったログイン transaction を NextAuth に渡してセッションを得る（ADR-0043）
  const completeCredentialsSignIn = async (transactionId: string) => {
    const result = await signIn('credentials', {
      transactionId,
      callbackUrl: returnUrl,
      redirect: false,
    });

    if (result?.error) {
      setTotpTransactionId(null);
      setError('サインインに失敗しました。もう一度お試しください。');
      setLoading(false);
    } else if (result?.ok) {
      // 成功時もスケジューラ経由で fire-once を保証し、監視 useEffect との二重発火を防ぐ
      schedulePostSignInRedirect(toPostSignInTarget(returnUrl));
    } else {
      // 予期しないレスポンス
      setLoading(false);
    }
  };

  const handleSignInFailure = (err: unknown) => {
    if (process.env.NODE_ENV === 'development') {
      logger.error('Credentials sign in error:', err);
    }
    setError(DEFAULT_SIGN_IN_ERROR);
    setLoading(false);
  };

  // Credentials 認証ハンドラー: パスワードを確かめ、TOTP が要れば確認コード入力へ進む
  const handleCredentialsSignIn = async (credentials: { email: string; password: string }) => {
    setLoading(true);
    setError(null);

    try {
      const { ok, data } = await postLoginStep('/api/auth/password/start', credentials);
      if (ok && data.transactionId && data.status === 'totp_required') {
        setTotpTransactionId(data.transactionId);
        setLoading(false);
        return;
      }
      if (ok && data.transactionId && data.status === 'ready') {
        await completeCredentialsSignIn(data.transactionId);
        return;
      }
      setError(data.message ?? DEFAULT_SIGN_IN_ERROR);
      setLoading(false);
    } catch (err) {
      handleSignInFailure(err);
    }
  };

  const handleTotpSubmit = async (code: string) => {
    if (!totpTransactionId) return;
    setLoading(true);
    setError(null);

    try {
      const { ok, data } = await postLoginStep('/api/auth/password/confirm', { transactionId: totpTransactionId, code });
      if (ok) {
        await completeCredentialsSignIn(totpTransactionId);
        return;
      }
      // コード違いは入力し直せる。期限切れ・ロックはパスワード入力からやり直す
      if (data.error !== 'INVALID_TOTP_CODE') {
        setTotpTransactionId(null);
      }
      setError(data.message ?? DEFAULT_SIGN_IN_ERROR);
      setLoading(false);
    } catch (err) {
      handleSignInFailure(err);
    }
  };

  const handleTotpCancel = () => {
    setTotpTransactionId(null);
    setError(null);
  };

  // ローディング中
  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center" role="status" aria-live="polite">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" aria-hidden="true"></div>
        <span className="sr-only">読み込み中...</span>
      </div>
    );
  }

  // 既に完全に認証済み（idとemailが存在する場合のみ）
  if (status === 'authenticated' && hasCompleteSession(session)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" role="status" aria-live="polite">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" aria-hidden="true"></div>
        <div className="text-center space-y-2">
          <p className="text-lg font-medium text-gray-900">リダイレクトします</p>
          <p className="text-sm text-gray-600">3 秒後に自動的にページが切り替わります</p>
          <p className="text-xs text-gray-500 mt-4">
            自動でページが切り替わらない場合は
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="ml-1 text-indigo-600 hover:text-indigo-800 underline"
            >
              リロード
            </button>
            してください
          </p>
        </div>
      </div>
    );
  }

  // 不完全なセッションまたは未認証の場合、サインインフォームを表示
  // status === 'authenticated' でも session.user.id がない場合はサインインフォームを表示

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* 招待コンテキストバナー */}
        {invitationToken && (
          <div className="mb-4">
            <NotificationBanner
              type="info"
              title="招待"
              message={invitationMessage}
            />
          </div>
        )}

        {/* 通知メッセージ */}
        {message && (
          <div className="mb-4">
            <NotificationBanner
              type="success"
              title="確認完了"
              message={message}
            />
          </div>
        )}

        {/* エラーメッセージ */}
        {error && (
          <div className="mb-4">
            <NotificationBanner
              type="error"
              title="エラー"
              message={error}
            />
          </div>
        )}

        {/* サインインフォーム（Credentials モード + SSO ボタン併設）。
            Issue #6149: `?org=<slug>` があれば公開 API の設定で見出し / 説明 /
            SSO ボタンラベル / パスワード欄非表示を反映する。
            `loginConfigLoading` の間は SignInForm の描画を保留し、
            hideCredentials=true 組織で通常フォームが一瞬見える flicker を防ぐ。 */}
        {loginConfigLoading ? (
          <div
            className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 min-h-[320px] flex items-center justify-center"
            role="status"
            aria-live="polite"
            data-testid="signin-config-loading"
          >
            <div
              className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"
              aria-hidden="true"
            />
            <span className="sr-only">サインイン画面を準備中...</span>
          </div>
        ) : totpTransactionId ? (
          <TotpChallengeForm loading={loading} onSubmit={handleTotpSubmit} onCancel={handleTotpCancel} />
        ) : (
          <SignInForm
            mode="credentials"
            loading={loading}
            onSubmit={handleCredentialsSignIn}
            onSsoSignIn={handleSsoSignIn}
            showSignUpLink={!!invitationToken}
            signUpHref={invitationToken ? `/auth/signup?invitation_token=${encodeURIComponent(invitationToken)}` : '/auth/signup'}
            loginTitle={loginPageConfig.title}
            loginDescription={loginPageConfig.description}
            ssoButtonLabel={loginPageConfig.ssoButtonLabel}
            hideCredentials={loginPageConfig.hideCredentials}
          />
        )}
      </div>
    </div>
  );
}

// ページ本体
export default function SignInPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <SignInContent />
    </Suspense>
  );
}
